"use client";
import { gql } from "@apollo/client";
import { atom, useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { unwrapGraphQLResponse } from "@/lib/nhost/client";
import { useNhost } from "@/lib/nhost/react";

type LoadState<T> =
  | ({ status: "INITIAL" | "LOADING" | "ERROR" } & { [K in keyof T]?: never })
  | ({ status: "SUCCESS" } & T);

type UserProfile = GQL_GET_USER_PROFILE_WITH_ORG_QUERY["user_profiles"][number];
type OrgSubscriptions = NonNullable<GQL_GET_ORG_SUBSCRIPTIONS_QUERY["orgs_by_pk"]>;

type OrgAtomType = LoadState<{
  userProfile: UserProfile;
  orgUser: UserProfile["org_user"];
  org: UserProfile["org_user"]["orgByOrg"];
  userId: string;
}>;

type SubscriptionsAtomType = LoadState<{
  durationInMinutes: number;
  credits: number | undefined;
  remainingCredits: number;
  subscriptions: OrgSubscriptions["org_subscriptions"];
  subscriptionPlan: string;
}>;

const initialOrgAtom: OrgAtomType = {
  status: "INITIAL",
};
const initialSubscriptionsAtom: SubscriptionsAtomType = {
  status: "INITIAL",
};

const orgAtom = atom<OrgAtomType>(initialOrgAtom);
const subscriptionsAtom = atom<SubscriptionsAtomType>(initialSubscriptionsAtom);

export const useOrg = (): OrgAtomType => {
  const [result] = useAtom(orgAtom);
  return result;
};

export const useSubscriptions = (): SubscriptionsAtomType => {
  const [result] = useAtom(subscriptionsAtom);
  return result;
};

export const useRefetchOrg = () => {
  const nhostClient = useNhost();
  const [atomData, setAtom] = useAtom(orgAtom);
  const handleFetch = async () => {
    // In v4, use getUserSession() for synchronous access to session data
    const session = nhostClient.getUserSession();
    const user = session?.user;
    if (!user) {
      throw new Error("No user found");
    }

    const getOrg = async ({ nRetry = 0 } = { nRetry: 0 }): Promise<UserProfile> => {
      const response = await nhostClient.graphql.request<
        GQL_GET_USER_PROFILE_WITH_ORG_QUERY,
        GQL_GET_USER_PROFILE_WITH_ORG_QUERY_VARIABLES
      >(
        gql`
          query GET_USER_PROFILE_WITH_ORG($user_id: uuid!) {
            user_profiles(where: { user: { _eq: $user_id } }) {
              org_user {
                orgByOrg {
                  id
                  title
                }
                id
              }
            }
          }
        `,
        { user_id: user.id }
      );

      const responseData = unwrapGraphQLResponse(response);

      const userProfile = responseData?.user_profiles[0];
      if (!userProfile) {
        console.warn("No org found for user", user.id);
        // When first signing up, the org and profile might not be created yet.
        // The JWT token won't have org_id until we refresh it after the event trigger creates the org.
        if (nRetry < 3) {
          await new Promise((res) => {
            setTimeout(() => {
              res(null);
            }, 3000);
          });
          // Force refresh the session to get a new JWT with updated org_id claims
          await nhostClient.refreshSession(0);
          // Recursive call - will either return data or throw after max retries
          return getOrg({ nRetry: nRetry + 1 });
        } else {
          throw new Error(`No org after ${nRetry} retries`);
        }
      }

      return userProfile;
    };

    const userProfile = await getOrg({ nRetry: 0 });

    // Force refresh the session to ensure the JWT has correct custom claims
    // (X-Hasura-Org-Ids, Org-Id, Org-User-Id). For new users, the initial JWT
    // is created before the event trigger populates org_users/user_profile,
    // so these claims may be empty. The getOrg retry loop only refreshes when
    // user_profiles isn't found, but the profile can be found on the first try
    // (via X-Hasura-User-Id) while Org-Ids is still empty.
    await nhostClient.refreshSession(0);

    const orgUser = userProfile.org_user;
    const org = orgUser.orgByOrg;
    setAtom({
      status: "SUCCESS",
      userProfile,
      orgUser,
      org,
      userId: user.id,
    });
  };
  return { refetch: handleFetch, status: atomData.status };
};

export const useInitiateOrg = () => {
  const [atomData, setAtom] = useAtom(orgAtom);
  const { refetch: handleFetch } = useRefetchOrg();

  const initiate = async () => {
    if (atomData.status !== "INITIAL") {
      return;
    }
    setAtom({ status: "LOADING" });
    try {
      await handleFetch();
    } catch (e) {
      console.error(e);
      setAtom({ status: "ERROR" });
    }
  };

  return { initiate, status: atomData.status };
};

export const useRefetchSubscriptions = () => {
  const t = useTranslations();
  const nhostClient = useNhost();
  const [atomData, setAtom] = useAtom(subscriptionsAtom);
  const handleFetch = async ({ orgId }: { orgId: string }) => {
    const response = await nhostClient.graphql.request<
      GQL_GET_ORG_SUBSCRIPTIONS_QUERY,
      GQL_GET_ORG_SUBSCRIPTIONS_QUERY_VARIABLES
    >(
      gql`
        query GET_ORG_SUBSCRIPTIONS($org_id: uuid!) {
          orgs_by_pk(id: $org_id) {
            id
            transcription_jobs_aggregate(
              where: { status: { _nin: ["INITIAL"] } }
            ) {
              aggregate {
                sum {
                  duration_ms
                }
              }
            }
            org_subscriptions {
              id
              plan
            }
            org_subscriptions_aggregate {
              aggregate {
                sum {
                  credits
                }
              }
            }
          }
        }
      `,
      { org_id: orgId }
    );
    const responseData = unwrapGraphQLResponse(response);
    if (!responseData?.orgs_by_pk) {
      console.warn("No org found for org", orgId);
      throw new Error("No org found for org");
    }
    const org = responseData.orgs_by_pk;
    const credits = org.org_subscriptions_aggregate.aggregate?.sum?.credits;
    const durationMs = org.transcription_jobs_aggregate.aggregate?.sum?.duration_ms;
    const durationInMinutes = (durationMs || 0) / 60 / 1000;
    const remainingCredits = (credits || 0) - durationInMinutes;

    let plan = t("subscriptions.noPlanLabel");
    for (const subscription of org.org_subscriptions) {
      if (subscription.plan !== "CREDITS") {
        plan = subscription.plan;
        break;
      }
    }

    setAtom({
      status: "SUCCESS",
      durationInMinutes: Math.ceil(durationInMinutes),
      credits: credits ?? undefined,
      remainingCredits: Math.floor(remainingCredits),
      subscriptions: org.org_subscriptions,
      subscriptionPlan: plan,
    });
  };
  return { refetch: handleFetch, status: atomData.status };
};

export const useInitiateSubscriptions = () => {
  const [atomData, setAtom] = useAtom(subscriptionsAtom);
  const { refetch: handleFetch } = useRefetchSubscriptions();

  const initiate = async ({ orgId }: { orgId: string }) => {
    if (atomData.status !== "INITIAL") {
      return;
    }
    setAtom({ status: "LOADING" });
    try {
      await handleFetch({ orgId });
    } catch (e) {
      console.error(e);
      setAtom({ status: "ERROR" });
    }
  };
  return { initiate, status: atomData.status };
};

export const useResetAtoms = () => {
  const [_orgAtom, setOrgAtom] = useAtom(orgAtom);
  const [_subscriptionsAtom, setSubscriptionsAtom] = useAtom(subscriptionsAtom);

  return () => {
    setOrgAtom(initialOrgAtom);
    setSubscriptionsAtom(initialSubscriptionsAtom);
  };
};
